import * as CoreActions from '@common/_store/core.actions';
import * as coreSelectors from '@common/_store/core.selectors';

import {Injectable} from '@angular/core';
import {firstValueFrom, map, Observable, of, Subject, take, tap} from 'rxjs';
import {LocalStorageService} from './local-storage.service';
import {ConfirmationDialogModel} from '../_models/core.models';
import {Store} from '@ngrx/store';
import {AuthenticationService} from './authentication.service';
import {NameResponse} from '../../../../services/chat/model/nameResponse';
import {HttpErrorResponse, HttpEvent} from '@angular/common/http';
import {DictionariesService} from '@common/_services/dictionaries.service';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '@common/_components/confirmation-dialog/confirmation-dialog.component';
import {ToastrService} from 'ngx-toastr';
import {StompNotificationsService} from '@common/_services/stomp-notifications.service';
import {v4} from 'uuid';
import {ConfigService} from '@common/_services/config.service';
import {WebLoginRequest} from '../../../../services/authentication/model/webLoginRequest';
import {WebTokenModel} from '../../../../services/authentication/model/webTokenModel';
import {BrokerUserProfile} from '../../../../services/broker/model/brokerUserProfile';
import {environment} from '../../../environments/environment';
import {Router} from '@angular/router';

@Injectable({
    providedIn: 'root'
})

export class CoreService {
    public httpError$ = new Subject<HttpErrorResponse>();

    constructor(
        private localStorageService: LocalStorageService,
        private authenticationService: AuthenticationService,
        private dictionariesService: DictionariesService,
        private toastr: ToastrService,
        private store$: Store,
        private dialog: MatDialog,
        private stompService: StompNotificationsService,
        private configService: ConfigService,
        private router: Router,
    ) {

    }

    setCurrentCabinetId(id: string) {
        this.localStorageService.setBrokerId(id);
        this.store$.dispatch(CoreActions.SetCurrentId({id}));
        this.setTabId();
        this.stompService.initWsConnection(this.localStorageService.get('accessToken') as string);
        this.setUserProfile();
    }

    selectCurrentCabinetId$(): Observable<string | undefined> {
        return this.store$.select(coreSelectors.getCabinetId);
    }

    setTabId() {
        const tabId: string = v4();
        this.store$.dispatch(CoreActions.SetTabId({tabId}));
    }

    selectTabId$(): Observable<string | undefined> {
        return this.store$.select(coreSelectors.getTabId);
    }

    getToken(token: string, request: WebLoginRequest): Observable<WebTokenModel> {
        return this.authenticationService.getTokenRequest(token, request).pipe(
            tap((resp: WebTokenModel) => {
                this.setAuthDataToLs(resp);
                this.setUserProfile();
            })
        );
    }

    updateToken(refreshToken: string): Observable<WebTokenModel> {
        return this.authenticationService.getRefreshTokenRequest(refreshToken).pipe(
            map((res: WebTokenModel) => {
                this.setAuthDataToLs(res);
                this.setUserProfile();
                return res;
            })
        );
    }

    setAuthDataToLs(authData: WebTokenModel) {
        this.localStorageService.setBrokerId(authData.broker!.id!);
        this.localStorageService.set('accessToken', authData.token!);
        this.localStorageService.set('refreshToken', authData.refreshToken!);
        this.localStorageService.set('expiresIn', String(authData.validUntilUtc!));
        this.localStorageService.set('deviceId', String(authData.deviceId!));
    }

    setUserProfile() {
        this.dictionariesService.getAuthUserProfile().pipe(
            take(1),
            tap((profile: BrokerUserProfile) => {
                this.store$.dispatch(CoreActions.LoadUserProfileSuccess({payload: profile}));
                this.setTokenPayloadToStore();
                if (profile.ownCompany?.frontCustomizationData) {
                    const portalColors: { primaryColor: string, secondaryColor: string } = JSON.parse(profile.ownCompany?.frontCustomizationData);
                    this.setColorTheme(portalColors.primaryColor, portalColors.secondaryColor);
                }
            })
        ).subscribe();
    }

    setColorTheme(primaryColor?: string, secondaryColor?: string) {
        document.documentElement.style.setProperty('--primary-color', primaryColor ? primaryColor : '#5685BC');
        document.documentElement.style.setProperty('--secondary-color', secondaryColor ? secondaryColor: '#C3394B');
    }

    setTokenPayloadToStore() {
        const accessToken = this.localStorageService.get('accessToken');
        const deviceId = this.localStorageService.get('deviceId');
        if (accessToken && deviceId) {
            this.store$.dispatch(CoreActions.SetTokenSuccess({payload: {accessToken, deviceId}}));
        }
    }

    navigateToLogin(sessionExpired?: boolean, changeCabinet?: boolean): Observable<HttpEvent<any>> {
        this.localStorageService.clear();
        this.dialog.closeAll();
        this.store$.dispatch(CoreActions.LogOutSuccess());

        if (sessionExpired) {
            this.toastr.error('Please, login again', 'Session expired');
        }

        if (environment.standaloneData.id) {
            this.router.navigate(['login']).then();
        } else {
            if (sessionExpired) {
                setTimeout(() => {
                    window.location.href = `${environment.homepageUrl}?relogin`;
                }, 3000);
            } else {
                window.location.href = `${environment.homepageUrl}${changeCabinet ? '?changeCabinet' : ''}`;
            }
        }

        return of();
    }

    selectCurrentUser$(): Observable<BrokerUserProfile | undefined> {
        return this.store$.select(coreSelectors.selectUserProfile);
    }

    setPageTitle(title: string) {
        return this.store$.dispatch(CoreActions.SetPageTitle({payload: title}));
    }

    selectPageTitle$(): Observable<string | undefined> {
        return this.store$.select(coreSelectors.selectPageTitle);
    }

    proceedHTTPError(rawError: HttpErrorResponse, key: string, data?: NameResponse) {
        this.httpError$.next(rawError);
        this.store$.dispatch(CoreActions.HttpErrorDetected({key, data}));
    }

    async showConfirmation(data: ConfirmationDialogModel, config?: MatDialogConfig): Promise<boolean | undefined> {
        const confirmationDialog$ = this.dialog.open(ConfirmationDialogComponent, {
            width: '100%',
            maxWidth: "380px",
            panelClass: ['confirm-dialog'],
            ...config && {...config},
            data
        });

        return firstValueFrom(confirmationDialog$.afterClosed()).then(data => {
            return data?.result;
        });
    }

    openDialog(component: any, config: MatDialogConfig, dialogData?: any) {
        return this.dialog.open(
            component,
            {
                ...this.configService.defaultDialogConfig, ...config,
                data: dialogData,
            },
        );
    }

    showToast(text: { message: string, title?: string }, type: 'info' | 'warning' | 'error' | 'success', params?: any) {
        switch (type) {
            case "success":
                break;
            case "warning":
                this.toastr.success(text.message, text?.title, params);
                break;
            case "error":
                break;
            case "info":
                break;
        }
    }
}
